import React, { useEffect } from "react";

/**
 * v1.25.52 — Pixabay-style "shards + 3D gold text" celebration overlay.
 * Reused by ThanksOverlay / InboxZeroOverlay / WelcomeOverlay so every
 * high-emotion moment in the app gets the same premium finish.
 *
 * Props:
 *   open              boolean — controls visibility
 *   onClose           callback fired on click / auto-dismiss
 *   title             the big 3D gold text (e.g. "THANK YOU")
 *   subtitle          optional supporting line under the title
 *   caption           tiny grey line at the bottom of the card
 *   ctaLabel          button label ("Done", "Keep going")
 *   palette           "gold" (default) | "amber" | "indigo" | "teal"
 *   shards            number of flying shards (default 14)
 *   autoDismissMs     default 3800ms
 *   testId            data-testid for the root
 */
const PALETTES = {
    gold:   { text: "#fde68a", edge: "#78350f", glow: "254, 202, 87",   bg: "from-slate-950 via-amber-950 to-slate-950" },
    amber:  { text: "#fed7aa", edge: "#7c2d12", glow: "251, 146, 60",   bg: "from-slate-950 via-orange-950 to-slate-950" },
    indigo: { text: "#c7d2fe", edge: "#312e81", glow: "129, 140, 248",  bg: "from-slate-950 via-indigo-950 to-slate-950" },
    teal:   { text: "#a7f3d0", edge: "#065f46", glow: "20, 184, 166",   bg: "from-slate-950 via-emerald-950 to-slate-950" },
};

const SHARD_COLORS_GOLD = ["#fde68a", "#facc15", "#f59e0b", "#fbbf24", "#fef3c7", "#eab308"];
const CONFETTI_COLORS   = ["#fde68a", "#f43f5e", "#22d3ee", "#a78bfa", "#34d399", "#fb923c"];

export default function FancyThanks({
    open,
    onClose,
    title = "THANK YOU",
    subtitle = "",
    caption = "",
    ctaLabel = "Done",
    palette = "gold",
    shards = 14,
    autoDismissMs = 3800,
    testId = "fancy-thanks-overlay",
}) {
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(onClose, autoDismissMs);
        return () => clearTimeout(t);
    }, [open, onClose, autoDismissMs]);

    if (!open) return null;
    const p = PALETTES[palette] || PALETTES.gold;
    const shardsArr = Array.from({ length: shards });
    const confetti = Array.from({ length: 26 });

    return (
        <div
            data-testid={testId}
            onClick={onClose}
            className={`fixed inset-0 z-[120] flex items-center justify-center bg-gradient-to-br ${p.bg} animate-in fade-in duration-500`}
            style={{ overflow: "hidden" }}
        >
            {/* Radial glow behind the text */}
            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(circle at 50% 45%, rgba(${p.glow}, 0.35) 0%, rgba(${p.glow}, 0.12) 30%, transparent 60%)`,
                    filter: "blur(6px)",
                }}
            />

            {/* Falling confetti */}
            {confetti.map((_, i) => {
                const left = 3 + (i * 3.7) % 94;
                const delay = (i % 9) * 90;
                const dur = 1600 + (i * 47) % 1400;
                const rot = (i * 53) % 360;
                const sz = 8 + (i * 3) % 10;
                const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
                return (
                    <span
                        key={`c-${i}`}
                        className="absolute rounded-sm"
                        style={{
                            left: `${left}%`, top: "-8%",
                            width: sz, height: sz * 1.6, background: color,
                            animation: `ft-fall ${dur}ms ease-in ${delay}ms forwards`,
                            transform: `rotate(${rot}deg)`,
                        }}
                    />
                );
            })}

            {/* Flying glass shards (SVG polygons that shoot outward from center) */}
            <svg
                aria-hidden
                className="absolute inset-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid slice"
                style={{ pointerEvents: "none" }}
            >
                {shardsArr.map((_, i) => {
                    const angle = (i / shards) * Math.PI * 2;
                    const dx = Math.cos(angle) * 55;
                    const dy = Math.sin(angle) * 55;
                    const rot = (i * 47) % 360;
                    const color = SHARD_COLORS_GOLD[i % SHARD_COLORS_GOLD.length];
                    const dur = 1200 + (i * 41) % 700;
                    const delay = (i % 6) * 55;
                    return (
                        <polygon
                            key={`s-${i}`}
                            points="0,-1.4 1.2,-0.4 0.6,1.4 -0.6,1.4 -1.2,-0.4"
                            fill={color}
                            opacity={0.85}
                            style={{
                                transformOrigin: "50px 50px",
                                animation: `ft-shard-${i} ${dur}ms cubic-bezier(.2,.7,.3,1) ${delay}ms forwards`,
                            }}
                            transform={`translate(50 50) rotate(${rot})`}
                        />
                    );
                })}
            </svg>

            {/* Sparkle sweep — a diagonal white band that streaks across the text */}
            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.10) 50%, transparent 60%)",
                    animation: "ft-sweep 1800ms ease-out 220ms forwards",
                }}
            />

            {/* 3D gold text + card */}
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 mx-4 flex w-full max-w-xl flex-col items-center text-center"
                style={{ animation: "ft-pop 700ms cubic-bezier(.34,1.56,.64,1) forwards" }}
            >
                <h2
                    className="select-none whitespace-nowrap font-display font-black uppercase tracking-tight"
                    style={{
                        fontSize: "clamp(2.5rem, 12vw, 6rem)",
                        lineHeight: 1,
                        color: p.text,
                        letterSpacing: "0.02em",
                        // 3D depth via layered text-shadows (dark edge behind, then progressively softer glow)
                        textShadow: [
                            `0 1px 0 ${p.edge}`,
                            `0 2px 0 ${p.edge}`,
                            `0 3px 0 ${p.edge}`,
                            `0 4px 0 ${p.edge}`,
                            `0 5px 0 ${p.edge}`,
                            `0 6px 1px rgba(0,0,0,0.15)`,
                            `0 0 20px rgba(${p.glow},0.55)`,
                            `0 0 45px rgba(${p.glow},0.35)`,
                        ].join(","),
                    }}
                >
                    {title}
                </h2>
                {subtitle && (
                    <p className="mt-6 max-w-md text-base font-medium text-white/90 sm:text-lg" style={{ animation: "ft-fade 900ms ease-out 400ms both" }}>
                        {subtitle}
                    </p>
                )}
                {caption && (
                    <p className="mt-2 max-w-md text-xs text-white/60" style={{ animation: "ft-fade 900ms ease-out 700ms both" }}>
                        {caption}
                    </p>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    data-testid={`${testId}-cta`}
                    className="mt-8 rounded-full bg-white/95 px-8 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-900 shadow-xl transition-transform hover:scale-105"
                    style={{ animation: "ft-fade 900ms ease-out 900ms both" }}
                >
                    {ctaLabel}
                </button>
            </div>

            <style>{`
                @keyframes ft-fall {
                    0%   { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
                    10%  { opacity: 1; }
                    100% { transform: translateY(120vh) rotate(900deg); opacity: 0.15; }
                }
                @keyframes ft-pop {
                    0%   { transform: scale(0.5) translateY(20px); opacity: 0; }
                    60%  { transform: scale(1.06) translateY(0);   opacity: 1; }
                    100% { transform: scale(1);    translateY(0);  opacity: 1; }
                }
                @keyframes ft-fade {
                    0%   { transform: translateY(6px); opacity: 0; }
                    100% { transform: translateY(0);   opacity: 1; }
                }
                @keyframes ft-sweep {
                    0%   { transform: translateX(-100%); opacity: 0; }
                    30%  { opacity: 1; }
                    100% { transform: translateX(100%);  opacity: 0; }
                }
                ${Array.from({ length: shards }).map((_, i) => {
                    const angle = (i / shards) * Math.PI * 2;
                    const dx = Math.cos(angle) * 60;
                    const dy = Math.sin(angle) * 60;
                    const endRot = (i * 47 + 720) % 900;
                    return `@keyframes ft-shard-${i} {
                        0%   { transform: translate(50px, 50px) rotate(0deg) scale(0.2);   opacity: 0; }
                        20%  { opacity: 1; }
                        100% { transform: translate(${50 + dx}px, ${50 + dy}px) rotate(${endRot}deg) scale(1); opacity: 0; }
                    }`;
                }).join("\n")}
            `}</style>
        </div>
    );
}
